import type { LenderYields } from '../../../../types/lenderTypes'

type RewardAprMap = Record<string, { borrowApr: number; depositApr: number; apr: number }>

/**
 * Per-reward yield after depositing `amountUSD`.
 */
export function getRewardYieldDeposit(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): RewardAprMap {
  const rewards = yieldParams?.rewards
  if (!Array.isArray(rewards) || rewards.length === 0) return {}

  const data: RewardAprMap = {}
  for (const reward of rewards) {
    const _flowOut = amountUSD * (reward.depositRate ?? 0)
    const totalCashFlowDeposits = cashFlowDeposits + _flowOut
    const newCollat = deposits + amountUSD
    data[reward.asset] = {
      borrowApr: Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
      depositApr: totalCashFlowDeposits / newCollat,
      apr: (cashFlowDeposits + cashFlowDebt + _flowOut) / (newCollat - debt),
    }
  }
  return data
}

/**
 * Per-reward yield after borrowing `amountUSD`.
 */
export function getRewardYieldBorrow(
  amountUSD: number,
  irMode: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): RewardAprMap {
  const rewards = yieldParams?.rewards
  if (!Array.isArray(rewards) || rewards.length === 0) return {}

  const data: RewardAprMap = {}
  for (const reward of rewards) {
    const _flowIn =
      amountUSD *
      ((irMode === 1
        ? reward.stableBorrowRate
        : reward.variableBorrowRate) ?? 0)
    const totalCashFlowDebt = cashFlowDebt + _flowIn
    const newDebt = debt + amountUSD
    data[reward.asset] = {
      borrowApr: totalCashFlowDebt / newDebt,
      depositApr: Math.abs(deposits) < 0.01 ? 0 : cashFlowDeposits / deposits,
      apr: (cashFlowDeposits + cashFlowDebt + _flowIn) / (deposits - newDebt),
    }
  }
  return data
}

/**
 * Per-reward yield after withdrawing `amountUSD`.
 */
export function getRewardYieldWithdraw(
  amountUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): RewardAprMap {
  const rewards = yieldParams?.rewards
  if (!Array.isArray(rewards) || rewards.length === 0) return {}

  const data: RewardAprMap = {}
  for (const reward of rewards) {
    const _flowIn = amountUSD * (reward.depositRate ?? 0)
    const totalCashFlowDeposits = cashFlowDeposits - _flowIn
    const newCollat = deposits - amountUSD
    data[reward.asset] = {
      borrowApr: Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
      depositApr: totalCashFlowDeposits / newCollat,
      apr: (cashFlowDeposits + cashFlowDebt - _flowIn) / (newCollat - debt),
    }
  }
  return data
}

/**
 * Per-reward yield after repaying `amountUSD`.
 */
export function getRewardYieldRepay(
  amountUSD: number,
  irMode: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldParams: LenderYields
): RewardAprMap {
  const rewards = yieldParams?.rewards
  if (!Array.isArray(rewards) || rewards.length === 0) return {}

  const data: RewardAprMap = {}
  for (const reward of rewards) {
    const _flowOut =
      amountUSD *
      ((irMode === 1
        ? reward.stableBorrowRate
        : reward.variableBorrowRate) ?? 0)
    const totalCashFlowDebt = cashFlowDebt - _flowOut
    const newDebt = debt - amountUSD
    data[reward.asset] = {
      borrowApr: Math.abs(newDebt) < 0.01 ? 0 : totalCashFlowDebt / newDebt,
      depositApr: Math.abs(deposits) < 0.01 ? 0 : cashFlowDeposits / deposits,
      apr: (cashFlowDeposits + cashFlowDebt - _flowOut) / (deposits - newDebt),
    }
  }
  return data
}
